import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1B4F72' } };
const HEADER_FONT = { bold: true, color: { argb: 'FFFFFF' }, size: 11 };
const TITLE_FONT = { bold: true, size: 14, color: { argb: '1B4F72' } };
const INFO_FONT = { bold: true, size: 11 };
const THIN_BORDER = {
  top: { style: 'thin' },
  bottom: { style: 'thin' },
  left: { style: 'thin' },
  right: { style: 'thin' }
};

const COLUMN_WIDTHS = [5, 22, 10, 18, 18, 18, 5, 5, 5, 18, 7, 16, 16, 12, 12, 5, 12];

const INFO_ROWS = [
  'ADHERENTS FUNTOA SMIE',
  'RAISON SOCIALE:',
  'NIF et STATISTIQUE:',
  'ADRESSE ET TELEPHONE:',
  "SECTEUR D'ACTIVITE:",
  ''
];

const HEADER_LABELS = [
  'Nb', "Lieu d'affectation", 'Mlle', 'NOM ', 'PRENOMS', 'Fonction',
  'A', 'C', 'E', 'Date de Naissance', 'Sexe', 'Date Embauche',
  'Date Debauche', 'Salaire', 'Cotisation', '', 'Total'
];

const EXAMPLES = [
  [1, 'SIEGE', '001', 'DOE', 'JOHN', 'COMPTABLE', 'X', '', '', '1988-05-17', 'M', '2020-01-10', '', '', '', '', ''],
  [2, '', '', 'DOE', 'JANE', '', '', 'X', '', '1990-09-23', 'F', '', '', '', '', '', ''],
  [3, '', '', 'DOE', 'JIM', '', '', '', 'X', '2015-03-08', 'M', '', '', '', '', '', ''],
  [4, '', '', 'DOE', 'JULIE', '', '', '', 'X', '2018-11-15', 'F', '', '', '', '', '', '']
];

const AGENT_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D6EAF8' } };
const DEP_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCF3CF' } };
const EFFECTIF_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E8F6F3' } };

const NOTES = [
  'Note',
  'A : Agent',
  'C : Conjoint(e)',
  'E : Enfant',
  '',
  "Veuillez cocher le service d'affiliation",
  'Veuillez bien compléter les cases A.C.E par le chiffre (1)'
];

/**
 * Génère et télécharge le modèle Excel d'import DNS (`modele_import_dns.xlsx`).
 *
 * @param {object} [options]
 * @param {string} [options.logoUrl='/logo FUNTOA.png'] - URL du logo (optionnel).
 * @returns {Promise<void>}
 */
export const downloadDnsTemplate = async ({ logoUrl = '/logo FUNTOA.png' } = {}) => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('MODELE_DNS');

  ws.columns = COLUMN_WIDTHS.map((width) => ({ width }));

  // Logo (optionnel : on continue silencieusement si indisponible)
  try {
    const logoResponse = await fetch(logoUrl);
    if (logoResponse.ok) {
      const logoBlob = await logoResponse.blob();
      const logoBuffer = await logoBlob.arrayBuffer();
      const logoId = wb.addImage({ buffer: logoBuffer, extension: 'png' });
      ws.addImage(logoId, {
        tl: { col: 0, row: 0 },
        ext: { width: 120, height: 80 }
      });
    }
  } catch {
    // Logo indisponible : on ignore
  }

  INFO_ROWS.forEach((text, i) => {
    const row = ws.getRow(i + 1);
    ws.mergeCells(i + 1, 2, i + 1, 17);
    const cell = row.getCell(2);
    cell.value = text;
    if (i === 0) {
      cell.font = TITLE_FONT;
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    } else {
      cell.font = INFO_FONT;
    }
    row.height = i === 0 ? 28 : 20;
  });

  const headerRowNum = 7;
  const headerRow = ws.getRow(headerRowNum);
  headerRow.height = 22;
  HEADER_LABELS.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.border = THIN_BORDER;
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  });

  EXAMPLES.forEach((data, i) => {
    const row = ws.getRow(headerRowNum + 1 + i);
    const isAgent = data[6] === 'X';
    data.forEach((val, j) => {
      const cell = row.getCell(j + 1);
      cell.value = val;
      cell.border = THIN_BORDER;
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.fill = isAgent ? AGENT_FILL : DEP_FILL;
    });
  });

  const footerStart = headerRowNum + 1 + EXAMPLES.length + 1;
  const effectifRows = [
    ['', 'EFFECTIF', '', 'Agents', '', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['', '', '', 'Conjoints', '', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['', '', '', 'Enfants', '', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['', '', '', 'TOTAL', '', '', '', '', '', '', '', '', '', '', '', '', '']
  ];

  effectifRows.forEach((data, i) => {
    const row = ws.getRow(footerStart + i);
    data.forEach((val, j) => {
      const cell = row.getCell(j + 1);
      cell.value = val;
      if (val) {
        cell.font = { bold: true, size: 11 };
        cell.fill = EFFECTIF_FILL;
      }
    });
    if (i === 0) {
      ws.getRow(footerStart).getCell(2).font = { bold: true, size: 12, color: { argb: '1B4F72' } };
    }
  });

  const noteStart = footerStart + effectifRows.length + 1;
  NOTES.forEach((text, i) => {
    const row = ws.getRow(noteStart + i);
    ws.mergeCells(noteStart + i, 2, noteStart + i, 6);
    const cell = row.getCell(2);
    cell.value = text;
    if (i === 0) {
      cell.font = { bold: true, size: 12, underline: true, color: { argb: 'C0392B' } };
    } else if (i >= 5) {
      cell.font = { bold: true, italic: true, size: 10, color: { argb: '7D3C98' } };
    } else {
      cell.font = { size: 10 };
    }
  });

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), 'modele_import_dns.xlsx');
};

export default downloadDnsTemplate;
